export const PROJECT_DOCUMENT_VERSION = 2 as const

/** Keeps positional layout metadata bounded to the engine/export particle budget. */
export const MAX_LAYOUT_OFFSET_COUNT = 100_000

/** Portable/runtime text bounds, measured in UTF-16 code units. */
export const MAX_PROJECT_TEXT_LENGTH = 10_000
export const MAX_ACTIVE_FONT_LENGTH = 256

/** Matches the editor's supported custom-canvas range. */
export const MIN_CANVAS_DIMENSION = 100
export const MAX_CANVAS_DIMENSION = 4_096

/**
 * Caps one logical canvas to a 4096×2160 allocation (about 34 MiB for RGBA8).
 * Render backends may impose a lower device-specific budget at runtime.
 */
export const MAX_CANVAS_PIXEL_AREA = MAX_CANVAS_DIMENSION * 2_160

export const ANIMATION_TYPES = [
  'linear',
  'spiral',
  'wave',
  'bounce',
  'cascade',
  'vortex',
  'gravity',
  'scatter',
  'elastic',
  'typewriter',
  'pen',
  'dots',
  'words',
  'bleed',
  'sandstorm',
  'magnet',
  'rain',
  'breathing',
  'shimmer',
  'flow',
] as const
export type AnimationType = (typeof ANIMATION_TYPES)[number]

/**
 * Bounds for the optional segmented animation timeline (shared contract).
 * The count cap keeps persisted timelines small; the weight cap keeps the
 * cumulative total finite so playback normalization cannot overflow.
 */
export const MAX_ANIMATION_SEGMENT_COUNT = 64
export const MAX_ANIMATION_SEGMENT_WEIGHT = 1_000_000

export const EASING_TYPES = [
  'linear',
  'smooth',
  'bounce',
  'elastic',
  'expo',
  'back',
  'cubic',
  'custom',
] as const
export type EasingType = (typeof EASING_TYPES)[number]

export const MODULE_SHAPES = [
  'x',
  'square',
  'circle',
  'diamond',
  'star',
  'dot',
  'triangle',
  'hexagon',
  'cross',
  'heart',
  'crescent',
  'ring',
  'svg',
] as const
export type ModuleShape = (typeof MODULE_SHAPES)[number]

export const TEXT_ALIGNS = ['right', 'center', 'left'] as const
export type TextAlign = (typeof TEXT_ALIGNS)[number]

export const TEXT_BASELINES = ['top', 'middle', 'bottom'] as const
export type TextBaseline = (typeof TEXT_BASELINES)[number]

export const PLAYBACK_DIRECTIONS = ['forward', 'reverse', 'pingPong'] as const
export type PlaybackDirection = (typeof PLAYBACK_DIRECTIONS)[number]

export const LOOP_MODES = ['loop', 'once'] as const
export type LoopMode = (typeof LOOP_MODES)[number]

export const RENDERER_PREFERENCES = ['webgl2', 'canvas2d'] as const
export type RendererPreference = (typeof RENDERER_PREFERENCES)[number]

export const NAMED_CANVAS_PRESET_DIMENSIONS = {
  '1920x1080': { width: 1_920, height: 1_080 },
  '1080x1920': { width: 1_080, height: 1_920 },
  '1280x720': { width: 1_280, height: 720 },
  '720x1280': { width: 720, height: 1_280 },
  '2560x1440': { width: 2_560, height: 1_440 },
  '1080x1080': { width: 1_080, height: 1_080 },
} as const

export type NamedCanvasSizePreset = keyof typeof NAMED_CANVAS_PRESET_DIMENSIONS

export const CANVAS_SIZE_PRESETS = [
  ...Object.keys(NAMED_CANVAS_PRESET_DIMENSIONS) as NamedCanvasSizePreset[],
  'custom',
] as const
export type CanvasSizePreset = NamedCanvasSizePreset | 'custom'

export interface RGBColor {
  r: number
  g: number
  b: number
}

export interface Offset2D {
  x: number
  y: number
}

export interface BezierControlPoint {
  x: number
  y: number
}

export interface CustomEasingCurve {
  p1: BezierControlPoint
  p2: BezierControlPoint
}

export interface CustomSvgData {
  dataUrl: string
  viewBox: {
    x: number
    y: number
    width: number
    height: number
  }
  name: string
}

export interface TextProjectSettings {
  content: string
  activeFont: string
  fontSize: number
  lineHeight: number
  wordSpacing: number
  textAlign: TextAlign
  baseline: TextBaseline
}

export interface ParticleProjectSettings {
  shape: ModuleShape
  density: number
  scale: number
  customSvg: CustomSvgData | null
}

export interface AppearanceProjectSettings {
  color: RGBColor
  chaosColor: RGBColor
  fadeInChaos: boolean
  gridMode: boolean
  colorTransitionStart: number
  colorTransitionEnd: number
  showBackground: boolean
  backgroundColor: RGBColor
  showBorder: boolean
  borderIntensity: number
  borderColor: RGBColor
  borderRadius: number
  borderOnTop: boolean
  borderScale: number
  showGlow: boolean
  glowIntensity: number
  glowColor: RGBColor
  glowSoftness: number
}

/**
 * One entry of the optional segmented animation timeline (shared contract).
 * `weight` must be positive and finite; weights are normalized at playback
 * into cumulative windows over the global eased progress.
 */
export interface AnimationSegment {
  readonly type: AnimationType
  readonly weight: number
}

export interface AnimationProjectSettings {
  type: AnimationType
  /**
   * Optional segmented timeline (shared contract). Absent/empty keeps the
   * legacy single-mode behavior; easing stays global across segments.
   */
  segments?: ReadonlyArray<AnimationSegment>
  speed: number
  easingType: EasingType
  easingAmount: number
  customEasingCurve: CustomEasingCurve
  playbackDirection: PlaybackDirection
  holdAtOrder: number
  holdAtChaos: number
  loopMode: LoopMode
  wiggleEnabled: boolean
  wiggleSpeed: number
  wiggleForce: number
  wiggleDampening: number
  rotationEnabled: boolean
  rotationSpeed: number
  /** Temporal trail strength. Zero disables motion blur; one uses the full shutter trail. */
  motionBlur: number
}

export interface LayoutProjectSettings {
  wordOffsets: Offset2D[]
  letterOffsets: Offset2D[]
  dotOffsets: Offset2D[]
  letterMode: boolean
  dotMode: boolean
}

export interface CanvasProjectSettings {
  width: number
  height: number
  preset: CanvasSizePreset
  preferredRenderer: RendererPreference
}

/**
 * Serializable user-authored project state.
 *
 * Playback clocks, selection, viewport transforms, renderer handles and derived
 * particle/layout buffers are deliberately excluded. Those values are session
 * state and must never make project files non-deterministic.
 */
export interface ProjectDocumentV2 {
  version: typeof PROJECT_DOCUMENT_VERSION
  seed: number
  text: TextProjectSettings
  particles: ParticleProjectSettings
  appearance: AppearanceProjectSettings
  animation: AnimationProjectSettings
  layout: LayoutProjectSettings
  canvas: CanvasProjectSettings
}
